import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteActions, SiteState } from '@next/core-lib/site';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { select, Store } from '@ngrx/store';
import { Observable, ReplaySubject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { selectFertilizationSettings } from 'src/app/advice/store/selectors';
import { CropFieldLegalStandardData } from 'src/app/legal-standard/models/legal-standard.model';
import { getFertilizationLegalStandards } from 'src/app/legal-standard/store/legal-standard.actions';
import {
  getCropYear,
  getReportSort,
  selectCropFieldAdvices,
  selectLoggedInEmployeeAsArray,
} from 'src/app/store/selectors';

import * as LegalStandardSelectors from '../../legal-standard/store/legal-standard.selectors';
import { ReportSort } from '../models';
import { ReportSortService } from '../services/report-sort.service';

@Component({
  selector: 'app-fertilization-legal-standards',
  templateUrl: './fertilization-legal-standards.component.html',
  styleUrls: ['./../reports.scss'],
  standalone: false,
})
export class FertilizationLegalStandardsComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly sortService = inject(ReportSortService);
  private readonly window = inject<WindowService>(WindowServiceInjector);
  private readonly router = inject(Router);

  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  relatedPeople$ = this.store.pipe(select(selectLoggedInEmployeeAsArray));
  mainCropFields$ = this.store.pipe(select(LegalStandardSelectors.selectMainCropFields));
  sortedMainCropFields$: Observable<CropFieldLegalStandardData[]> = new Observable();
  subsequentCultivations$ = this.store.pipe(
    select(LegalStandardSelectors.selectSubsequentCultivations),
  );
  sortedSubsequentCultivations$: Observable<CropFieldLegalStandardData[]> = new Observable();
  legalStandardBalance$ = this.store.pipe(
    select(LegalStandardSelectors.selectLegalStandardBalance),
  );
  cropFieldAdvices$ = this.store.pipe(select(selectCropFieldAdvices));
  fertilizationSettings$ = this.store.pipe(select(selectFertilizationSettings));

  readonly reportTitle = this.locale.translate('fertilizationLegalStandardsReportTitle');
  pageTitle$ = this.store.pipe(
    select(
      LegalStandardSelectors.selectFertilizationLegalStandardsReportTitle({
        reportTitle: this.reportTitle,
      }),
    ),
  );

  reportSort$ = this.store.pipe(select(getReportSort));

  buttonItems$ = this.sortService.getButtonItems();

  cropYear$ = this.store.pipe(select(getCropYear));

  ngOnInit() {
    this.pageTitle$.pipe(takeUntil(this.destroyed$)).subscribe((pageTitle) => {
      this.store.dispatch(SiteActions.updatePageTitle({ pageTitle }));
    });

    this.reportSort$.subscribe((reportSort) => {
      this.sortedMainCropFields$ = this.mainCropFields$.pipe(
        map((mainCropFields) => this.sortCropFields(mainCropFields, reportSort)),
      );
      this.sortedSubsequentCultivations$ = this.subsequentCultivations$.pipe(
        map((subsequentCultivations) => this.sortCropFields(subsequentCultivations, reportSort)),
      );
    });

    combineLatest([this.cropFieldAdvices$, this.fertilizationSettings$, this.legalStandardBalance$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([cropFieldAdvices, fertilizationSettings, legalStandardBalance]) => {
        if (cropFieldAdvices && fertilizationSettings && !legalStandardBalance) {
          this.store.dispatch(getFertilizationLegalStandards());
        }
      });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  print() {
    this.window.print();
  }

  back() {
    this.router.navigate(['/']);
  }

  buttonAction(data: string) {
    this.sortService.handleButtonAction(data);
  }

  hasSuccessBg = (value: number): boolean => value >= 0;
  hasWarnBg = (value: number): boolean => value && value < 0;

  private sortCropFields = (
    cropFields: CropFieldLegalStandardData[],
    reportSort: ReportSort,
  ): CropFieldLegalStandardData[] => {
    if (cropFields) {
      return this.sortService.sort({
        elements: cropFields,
        option: reportSort.option,
        descending: reportSort.descending,
        cropFieldNameFunc: (f) => f.cropFieldName,
        cropNameFunc: (f) => `${f.cropName} ${f.cropVarietyName}`,
      });
    }
  };
}
